import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to fetch static JSON as fallback when backend is asleep
const fetchStaticJSON = async (filename) => {
  try {
    const response = await fetch(`/${filename}`);
    if (!response.ok) throw new Error('Static file not found');
    return await response.json();
  } catch (error) {
    console.error(`Error fetching static ${filename}:`, error);
    return [];
  }
};

// Products API
export const productsAPI = {
  // Get all products - tries API first, falls back to static JSON
  getAll: async () => {
    try {
      const response = await axios.get(`${API}/products`, { timeout: 5000 });
      return response.data;
    } catch (error) {
      console.warn('Backend unavailable, loading from static JSON...');
      return await fetchStaticJSON('products.json');
    }
  },

  // Get single product
  getOne: async (id) => {
    try {
      const response = await axios.get(`${API}/products/${id}`, { timeout: 5000 });
      return response.data;
    } catch (error) {
      // Fallback: find in static JSON
      const products = await fetchStaticJSON('products.json');
      return products.find(p => p.id === id) || null;
    }
  },

  // Create product (only works when backend is awake)
  create: async (productData) => {
    try {
      const response = await axios.post(`${API}/products`, productData);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // Update product
  update: async (id, productData) => {
    try {
      const response = await axios.put(`${API}/products/${id}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Delete product
  delete: async (id) => {
    try {
      const response = await axios.delete(`${API}/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // Upload image
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Upload response:', response.data);
      const imageUrl = response.data.url;
      console.log('Image URL from backend:', imageUrl);
      
      return imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }
};

// Catalogs API
export const catalogsAPI = {
  getAll: async () => {
    try {
      const response = await axios.get(`${API}/catalogs`, { timeout: 5000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching catalogs:', error);
      return [];
    }
  },

  create: async (catalogData) => {
    try {
      const response = await axios.post(`${API}/catalogs`, catalogData);
      return response.data;
    } catch (error) {
      console.error('Error creating catalog:', error);
      throw error;
    }
  },

  update: async (id, catalogData) => {
    try {
      const response = await axios.put(`${API}/catalogs/${id}`, catalogData);
      return response.data;
    } catch (error) {
      console.error('Error updating catalog:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await axios.delete(`${API}/catalogs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting catalog:', error);
      throw error;
    }
  }
};

// Production Items API
export const productionItemsAPI = {
  // Get all production items - tries API first, falls back to static JSON
  getAll: async () => {
    try {
      const response = await axios.get(`${API}/production-items`, { timeout: 5000 });
      return response.data;
    } catch (error) {
      console.warn('Backend unavailable, loading production items from static JSON...');
      return await fetchStaticJSON('production-items.json');
    }
  },

  // Create production item
  create: async (itemData) => {
    try {
      const response = await axios.post(`${API}/production-items`, itemData);
      return response.data;
    } catch (error) {
      console.error('Error creating production item:', error);
      throw error;
    }
  },

  // Update production item
  update: async (id, itemData) => {
    try {
      const response = await axios.put(`${API}/production-items/${id}`, itemData);
      return response.data;
    } catch (error) {
      console.error('Error updating production item:', error);
      throw error;
    }
  },

  // Delete production item
  delete: async (id) => {
    try {
      const response = await axios.delete(`${API}/production-items/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting production item:', error);
      throw error;
    }
  }
};

export default productsAPI;
